// ─────────────────────────────────────────────────────────────────────────
// LoginSplit.js
//
// Alternate login layout — a two-pane "Welcome Back" card on the left
// and a rotating image carousel with marketing copy on the right. Same
// auth backend (`/login`), same first-login + license + branch flows,
// just a different skin. Switched on/off per facility via the
// `login_style_split` flag in facility_settings.
//
// All shared behaviour (POST, license/branch error mapping, post-login
// socket + navigation) is delegated to `loginCore.js` so this file
// stays focused on layout.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, InputAdornment, IconButton, Typography,
  CircularProgress, Alert,
} from '@mui/material';
import {
  Person as UserIcon,
  Lock as LockIcon,
  Visibility, VisibilityOff,
  CheckCircleOutline as CheckIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';

import { socket } from '../services/socket';
import { submitCredentials, finalizeLogin } from './loginCore';
import FirstLoginDialog from '../FirstLoginDialog ';
import { companyInfo, loginSlidesAPI, facilitySettingsAPI } from '../BaseURL';
import { originalPhotosFromSettings } from '../../utils/facilityPhotos';
import axios from 'axios';

// ── Carousel content ─────────────────────────────────────────────────────
// DEFAULT_SLIDES are neutral illustrations bundled in /public/login-defaults
// — no real building or staff — so any facility can use them as-is. When a
// facility uploads its own pictures (Admin → Hospital Admin Settings →
// Login Page Images) those replace the defaults entirely. A custom slide
// without a title/caption borrows the default copy at the same position.
export const DEFAULT_SLIDES = [
  {
    img: '/login-defaults/diagnostics.svg',
    title: 'Excellence in Medical Diagnostics',
    body:  'Providing accurate, reliable, and timely laboratory results with state-of-the-art technology and expert care.',
  },
  {
    img: '/login-defaults/laboratory.svg',
    title: 'State-of-the-art Laboratory',
    body:  'Modern analyzers, automated workflows, and stringent QC keep every result trustworthy from sample to report.',
  },
  {
    img: '/login-defaults/care.svg',
    title: 'Trusted Clinical Care',
    body:  'A multidisciplinary team committed to compassion, accuracy, and confidentiality at every step of the patient journey.',
  },
  {
    img: '/login-defaults/results.svg',
    title: 'Fast & Accurate Results',
    body:  'Streamlined sample handling and digital reporting put validated results in the doctor\'s hands without delay.',
  },
  {
    img: '/login-defaults/patient.svg',
    title: 'Patient-First Experience',
    body:  'From front desk to phlebotomy to portal — every touchpoint is designed around the patient\'s comfort and convenience.',
  },
  {
    img: '/login-defaults/workflow.svg',
    title: 'Integrated Hospital Workflow',
    body:  'OPD, Records, Pharmacy, Lab and Accounts share a single system so nothing falls between the cracks.',
  },
  {
    img: '/login-defaults/secure.svg',
    title: 'Secure & Compliant',
    body:  'Role-based access, full audit trails and encrypted transport — patient data stays where it should.',
  },
];

// The original facility photos in /public/pic, kept for the site they were
// taken at. Used instead of DEFAULT_SLIDES when the facility setting
// `login_use_original_photos` is on and nothing has been uploaded.
export const ORIGINAL_PHOTO_SLIDES = DEFAULT_SLIDES.map((s, i) => ({
  ...s,
  img: `/pic/pic${i + 1}.jpeg`,
}));

// Maps /loginSlides rows onto the carousel shape; empty → fallback set.
function toSlides(rows, fallbackSet = DEFAULT_SLIDES) {
  if (!Array.isArray(rows) || rows.length === 0) return fallbackSet;
  return rows.map((r, i) => {
    const fallback = DEFAULT_SLIDES[i % DEFAULT_SLIDES.length];
    return {
      img:   r.image,
      title: r.title || fallback.title,
      body:  r.body  || fallback.body,
    };
  });
}

const BULLETS = [
  'Advanced Diagnostic Equipment',
  'Certified Laboratory Professionals',
  'Fast & Accurate Results',
];

// Slide rotation interval — long enough for the eye to land on a single
// frame but short enough to keep the page feeling alive.
const ROTATE_MS = 5500;

export default function LoginSplit() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPwd,   setShowPwd]   = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [success,   setSuccess]   = useState(false);
  const [error,     setError]     = useState('');

  // First-login dialog state — same dialog component used by the classic
  // login so the credential-reset flow is consistent across both skins.
  const [showFirstLogin,  setShowFirstLogin]  = useState(false);
  const [firstLoginEmail, setFirstLoginEmail] = useState('');
  const [pendingUserData, setPendingUserData] = useState(null);

  // Company branding (logo + name) for the top-left chip.
  const [company, setCompany] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(companyInfo);
        if (alive && data?.success && Array.isArray(data.results) && data.results.length) {
          setCompany(data.results[0]);
        }
      } catch (_) { /* logo fallback handles it */ }
    })();
    return () => { alive = false; };
  }, []);

  // Facility's own login pictures. null = still loading, so the pane stays
  // dark instead of flashing the defaults before custom images arrive.
  const [slides, setSlides] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const [slidesRes, settingsRes] = await Promise.allSettled([
        axios.get(loginSlidesAPI, { timeout: 8000 }),
        axios.get(facilitySettingsAPI, { timeout: 8000 }),
      ]);
      if (!alive) return;
      const settings = settingsRes.status === 'fulfilled' ? settingsRes.value.data : null;
      const fallbackSet = originalPhotosFromSettings(settings) ? ORIGINAL_PHOTO_SLIDES : DEFAULT_SLIDES;
      const rows = slidesRes.status === 'fulfilled' && slidesRes.value.data?.success
        ? slidesRes.value.data.results
        : [];
      setSlides(toSlides(rows, fallbackSet));
    })();
    return () => { alive = false; };
  }, []);
  const SLIDES = slides || DEFAULT_SLIDES;

  // ── Carousel index — advances on a timer; clears + restarts when the
  // user clicks a dot so the manual frame stays put for a full cycle. ──
  const [slideIdx, setSlideIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setSlideIdx((i) => (i + 1) % SLIDES.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [slideIdx, SLIDES.length]);

  const activeSlide = useMemo(() => SLIDES[slideIdx % SLIDES.length], [SLIDES, slideIdx]);

  // ── Submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username.trim() || !formData.password) {
      setError('Enter both your username and password.');
      return;
    }
    setError('');
    setLoading(true);

    const result = await submitCredentials({
      username: formData.username.trim(),
      password: formData.password,
    });

    if (result.kind === 'first-login') {
      setFirstLoginEmail(result.user.email);
      setPendingUserData(result.user);
      setShowFirstLogin(true);
      setLoading(false);
      return;
    }

    if (result.kind === 'branch-blocked') {
      setLoading(false);
      await Swal.fire({
        icon: 'warning',
        title: 'Branch not active',
        text: result.message,
        confirmButtonColor: '#b45309',
      });
      return;
    }

    if (result.kind === 'license-blocked') {
      setLoading(false);
      await Swal.fire({
        icon: 'error',
        title: '🔒 System License Expired',
        text: result.message,
        confirmButtonColor: '#d33',
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      return;
    }

    if (result.kind === 'error') {
      setLoading(false);
      setError(result.message);
      return;
    }

    // result.kind === 'ok'
    if (result.licenseWarning && result.daysLeft) {
      const d = result.daysLeft;
      const urgencyColor = d <= 7 ? '#dc2626' : d <= 30 ? '#ea580c' : '#d97706';
      await Swal.fire({
        icon: d <= 30 ? 'error' : 'warning',
        title: d <= 7 ? '⚠️ License Expiring Very Soon!' : '📅 License Expiry Notice',
        html: `
          <div style="text-align:center;padding:8px 0">
            <div style="display:inline-block;background:${urgencyColor};color:#fff;font-size:40px;font-weight:900;padding:12px 28px;border-radius:12px;margin-bottom:12px">${d}</div>
            <p style="color:#374151;font-size:15px;font-weight:600;margin:0 0 6px">day${d === 1 ? '' : 's'} remaining</p>
            <p style="color:#6b7280;font-size:13px;margin:0 0 14px">Your TotalCare system license will expire soon.</p>
          </div>
        `,
        confirmButtonText: 'Continue to Dashboard',
        confirmButtonColor: urgencyColor,
        allowOutsideClick: false,
      });
    }

    finalizeLogin(result.user, { navigate, socket, setSuccess });
  };

  // First-login dialog success path matches the classic Login: clear the
  // password, surface a success toast, and let the user sign in again
  // with the new credentials. We deliberately don't auto-finalize the
  // login here — that's how the classic flow works and we keep parity.
  const handleFirstLoginSuccess = () => {
    setShowFirstLogin(false);
    setFormData({ username: formData.username, password: '' });
    setLoading(false);
    Swal.fire({
      title: 'Password changed',
      text: 'Please sign in with your new password.',
      icon: 'success',
      confirmButtonColor: '#3b82f6',
    });
  };

  // ──────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#000000', overflow: 'hidden' }}>
      {/* Per-slide keyframes for the slow Ken-Burns zoom on the background
          image. Re-keyed on slideIdx so each new slide animates fresh. */}
      <style>{`
        @keyframes lsKen { from { transform: scale(1.04) } to { transform: scale(1.14) } }
        @keyframes lsFade { from { opacity: 0 } to { opacity: 1 } }
      `}</style>

      {/* ══════════════════════════════════════════════════════════════════
          LEFT PANE — login form
      ══════════════════════════════════════════════════════════════════ */}
      <Box sx={{
        width: { xs: '100%', md: '46%', lg: '40%' },
        bgcolor: '#000000',
        color: '#fff',
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        px: { xs: 3, md: 6 }, py: { xs: 4, md: 6 },
        borderRight: { md: '1px solid rgba(255,255,255,0.04)' },
      }}>
        {/* Brand chip — logo (or a fallback science glyph) + company name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: { xs: 5, md: 8 } }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg,#1e3a8a 0%,#3b82f6 100%)',
            overflow: 'hidden',
          }}>
            {company?.logo
              ? <img alt="logo" src={company.logo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <ScienceIcon sx={{ color: '#fff', fontSize: 22 }} />}
          </Box>
          {company?.name && (
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, letterSpacing: '0.4px' }}>
              {company.name}
            </Typography>
          )}
        </Box>

        {/* Form card — vertically centred on tall viewports, flowing on short */}
        <Box sx={{
          flex: 1,
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          maxWidth: 420, width: '100%', mx: { xs: 'auto', md: 0 },
        }}>
          <Typography sx={{ fontSize: { xs: '2rem', md: '2.4rem' }, fontWeight: 800, lineHeight: 1.1, mb: 1 }}>
            Welcome Back
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.95rem', mb: 4 }}>
            Sign in to access your account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5, bgcolor: 'rgba(239,68,68,0.08)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.24)' }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} autoComplete="on">
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.62)', mb: 0.75, fontWeight: 600 }}>
              Username
            </Typography>
            <TextField
              fullWidth
              size="medium"
              placeholder="Enter your username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <UserIcon sx={{ color: 'rgba(255,255,255,0.42)', fontSize: 20 }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: '#0F1115',
                  color: '#fff',
                  borderRadius: 1.5,
                  transition: 'box-shadow 180ms ease, border-color 180ms ease',
                  '& fieldset':              { borderColor: 'rgba(255,255,255,0.10)' },
                  '&:hover fieldset':        { borderColor: 'rgba(59,130,246,0.55)' },
                  '&.Mui-focused':           { boxShadow: '0 0 0 4px rgba(59,130,246,0.22)' },
                  '&.Mui-focused fieldset':  { borderColor: '#3b82f6', borderWidth: 1.5 },
                },
              }}
              sx={{ mb: 2 }}
              autoFocus
            />

            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.62)', mb: 0.75, fontWeight: 600 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              size="medium"
              type={showPwd ? 'text' : 'password'}
              placeholder="Enter Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'rgba(255,255,255,0.42)', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPwd((s) => !s)}
                      sx={{ color: 'rgba(255,255,255,0.42)' }}
                    >
                      {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: '#0F1115',
                  color: '#fff',
                  borderRadius: 1.5,
                  transition: 'box-shadow 180ms ease, border-color 180ms ease',
                  '& fieldset':              { borderColor: 'rgba(255,255,255,0.10)' },
                  '&:hover fieldset':        { borderColor: 'rgba(59,130,246,0.55)' },
                  '&.Mui-focused':           { boxShadow: '0 0 0 4px rgba(59,130,246,0.22)' },
                  '&.Mui-focused fieldset':  { borderColor: '#3b82f6', borderWidth: 1.5 },
                },
              }}
              sx={{ mb: 1 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
              <Typography
                component="a"
                href="#forgot"
                onClick={(e) => {
                  e.preventDefault();
                  Swal.fire({
                    icon: 'info',
                    title: 'Forgot password?',
                    text: 'Contact your system administrator to reset your password.',
                    confirmButtonColor: '#3b82f6',
                  });
                }}
                sx={{
                  fontSize: '0.78rem', fontWeight: 700,
                  color: '#3b82f6', textDecoration: 'none',
                  letterSpacing: '0.4px', textTransform: 'uppercase',
                  '&:hover': { color: '#60a5fa' },
                }}
              >
                Forgot password?
              </Typography>
            </Box>

            <Button
              type="submit"
              fullWidth
              disabled={loading || success}
              sx={{
                py: 1.5,
                borderRadius: 1.5,
                textTransform: 'uppercase',
                fontWeight: 800, letterSpacing: '0.8px',
                color: '#fff',
                background: 'linear-gradient(180deg,#3b82f6 0%,#2563eb 100%)',
                boxShadow: '0 10px 28px rgba(59,130,246,0.35)',
                '&:hover':    { background: '#2563eb' },
                '&.Mui-disabled': {
                  background: 'rgba(59,130,246,0.5)',
                  color: 'rgba(255,255,255,0.85)',
                },
              }}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
            >
              {loading ? 'Signing in…' : success ? 'Welcome!' : 'Sign in'}
            </Button>
          </form>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
              <strong style={{ color: '#fff' }}>Patient?</strong>{' '}
              <Typography
                component="a"
                href="#patient-portal"
                onClick={(e) => {
                  e.preventDefault();
                  Swal.fire({
                    icon: 'info',
                    title: 'Patient Portal',
                    text: 'The patient portal sign-in will be available shortly. Please contact reception in the meantime.',
                    confirmButtonColor: '#3b82f6',
                  });
                }}
                sx={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 700, '&:hover': { color: '#60a5fa' } }}
              >
                Sign in to Patient Portal
              </Typography>
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.36)', mt: 1 }}>
              Need help? Contact your administrator for account access.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════
          RIGHT PANE — image carousel + headline / bullets
          Hidden under md so phones get a clean centred login.
      ══════════════════════════════════════════════════════════════════ */}
      <Box sx={{
        flex: 1,
        position: 'relative',
        display: { xs: 'none', md: 'block' },
        overflow: 'hidden',
      }}>
        {/* Background slide — keyed on slideIdx so the fade + Ken-Burns
            zoom restart on each rotation. */}
        {slides && <Box
          key={slideIdx}
          sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `url("${activeSlide.img}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: `lsFade 900ms ease-out, lsKen ${ROTATE_MS}ms linear forwards`,
          }}
        />}

        {/* Dark gradient so the headline copy is legible on any image.
            Deeper at the foot of the pane (where the copy sits) and a
            soft black wash on the left edge to meet the pitch-black
            login pane without a visible seam. */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.88) 100%)',
        }} />
        <Box sx={{
          position: 'absolute', top: 0, bottom: 0, left: 0, width: 120,
          background: 'linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
          pointerEvents: 'none',
        }} />

        {/* Headline block — vertically centred on the photo so the copy
            sits in the middle of the pane (matches the reference image,
            and lets the marketing message own the eye-line). Animates
            in on slide change too. */}
        {slides && <Box
          key={`copy-${slideIdx}`}
          sx={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            color: '#fff',
            px: { md: 6, lg: 8 },
            py: { md: 4, lg: 6 },
            animation: 'lsFade 700ms ease-out',
          }}
        >
          <Typography sx={{
            fontSize: { md: '2.8rem', lg: '3.4rem' },
            fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.5px',
            maxWidth: 720,
            textShadow: '0 3px 18px rgba(0,0,0,0.65)',
          }}>
            {activeSlide.title}
          </Typography>
          <Typography sx={{
            mt: 2, mb: 3, maxWidth: 620, color: 'rgba(255,255,255,0.9)',
            fontSize: '1rem', lineHeight: 1.55,
            textShadow: '0 1px 8px rgba(0,0,0,0.45)',
          }}>
            {activeSlide.body}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {BULLETS.map((b) => (
              <Box key={b} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon sx={{ color: '#34d399', fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 600 }}>{b}</Typography>
              </Box>
            ))}
          </Box>

          {/* Carousel dots — click jumps the slide. */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {SLIDES.map((_, i) => (
              <Box
                key={i}
                onClick={() => setSlideIdx(i)}
                sx={{
                  width: i === slideIdx ? 26 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: i === slideIdx ? '#3b82f6' : 'rgba(255,255,255,0.45)',
                  cursor: 'pointer',
                  transition: 'all 250ms ease',
                  '&:hover': { bgcolor: i === slideIdx ? '#3b82f6' : 'rgba(255,255,255,0.7)' },
                }}
              />
            ))}
          </Box>
        </Box>}
      </Box>

      {/* First-login (force-reset) dialog — same component the classic
          login uses, so password reset UX stays identical. Props mirror
          the classic Login's invocation. */}
      <FirstLoginDialog
        open={showFirstLogin}
        email={firstLoginEmail}
        trans_code={pendingUserData?.id}
        onClose={() => { setShowFirstLogin(false); setLoading(false); }}
        onSuccess={handleFirstLoginSuccess}
      />
    </Box>
  );
}
